import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { extraAdvancesService, recurringBonusesService, userService } from '../services/api';
import { DollarSign, Plus, Edit, Trash2, Save, X, Calendar, User, AlertCircle, CheckCircle, XCircle, RefreshCw, ToggleLeft, ToggleRight } from 'lucide-react';
import toast from 'react-hot-toast';
import ConfirmModal from './ConfirmModal';

const ExtraAdvancesManagement = () => {
  const queryClient = useQueryClient();
  const [selectedUserId, setSelectedUserId] = useState('');
  const now = new Date();
  const currentMonth = now.getMonth() + 1;
  const currentYear = now.getFullYear();
  const previousMonth = currentMonth === 1 ? 12 : currentMonth - 1;
  const previousMonthYear = currentMonth === 1 ? currentYear - 1 : currentYear;

  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingAdvance, setEditingAdvance] = useState(null);
  const [formData, setFormData] = useState({
    amount: '',
    request_date: now.toISOString().split('T')[0],
    deduction_month: previousMonth,
    deduction_year: previousMonthYear,
    description: '',
    notes: '',
  });

  // Recurring bonus state
  const [isRecurringModalOpen, setIsRecurringModalOpen] = useState(false);
  const [editingRecurring, setEditingRecurring] = useState(null);
  const [recurringFormData, setRecurringFormData] = useState({
    userId: '',
    amount: '',
    description: '',
    startMonth: currentMonth,
    startYear: currentYear,
    endMonth: '',
    endYear: '',
  });

  // Confirm modal state
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [confirmDeleteRecurring, setConfirmDeleteRecurring] = useState(null);

  // Buscar funcionários ativos
  const { data: usersData } = useQuery({
    queryKey: ['users-for-extra-advances'],
    queryFn: () => userService.getUsers({ limit: 100, active: true }),
    staleTime: 5 * 60 * 1000,
  });

  const users = useMemo(() => {
    if (!usersData) return [];
    if (Array.isArray(usersData?.data?.users)) return usersData.data.users;
    if (Array.isArray(usersData?.data?.data?.users)) return usersData.data.data.users;
    if (Array.isArray(usersData?.data)) return usersData.data;
    return [];
  }, [usersData]);

  // Buscar bônus
  const { data: advancesData, isLoading, refetch } = useQuery({
    queryKey: ['extra-advances', selectedUserId, selectedYear, selectedMonth],
    queryFn: () =>
      extraAdvancesService.getExtraAdvances({
        userId: selectedUserId || undefined,
        deductionYear: selectedYear,
        deductionMonth: selectedMonth,
      }),
    staleTime: 0,
  });

  const advances = useMemo(() => {
    if (!advancesData?.data?.data) return [];
    return Array.isArray(advancesData.data.data) ? advancesData.data.data : [];
  }, [advancesData]);

  // Mutação para criar bônus
  const createMutation = useMutation({
    mutationFn: (data) => extraAdvancesService.createExtraAdvance(data),
    onSuccess: () => {
      toast.success('Bônus criado com sucesso!');
      queryClient.invalidateQueries({ queryKey: ['extra-advances'] });
      queryClient.invalidateQueries({ queryKey: ['payslips'] });
      setIsModalOpen(false);
      resetForm();
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao criar bônus');
    },
  });

  // Mutação para atualizar bônus
  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => extraAdvancesService.updateExtraAdvance(id, data),
    onSuccess: () => {
      toast.success('Bônus atualizado com sucesso!');
      queryClient.invalidateQueries({ queryKey: ['extra-advances'] });
      queryClient.invalidateQueries({ queryKey: ['payslips'] });
      setIsModalOpen(false);
      setEditingAdvance(null);
      resetForm();
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao atualizar bônus');
    },
  });

  // Mutação para deletar bônus
  const deleteMutation = useMutation({
    mutationFn: (id) => extraAdvancesService.deleteExtraAdvance(id),
    onSuccess: () => {
      toast.success('Bônus deletado com sucesso!');
      queryClient.invalidateQueries({ queryKey: ['extra-advances'] });
      queryClient.invalidateQueries({ queryKey: ['payslips'] });
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao deletar bônus');
    },
  });

  // Mutação para aprovar bônus
  const approveMutation = useMutation({
    mutationFn: (id) => extraAdvancesService.updateExtraAdvance(id, { status: 'APPROVED' }),
    onSuccess: () => {
      toast.success('Bônus aprovado com sucesso!');
      queryClient.invalidateQueries({ queryKey: ['extra-advances'] });
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao aprovar bônus');
    },
  });

  // Mutação para cancelar/rejeitar bônus
  const rejectMutation = useMutation({
    mutationFn: (id) => extraAdvancesService.updateExtraAdvance(id, { status: 'CANCELLED' }),
    onSuccess: () => {
      toast.success('Bônus cancelado com sucesso!');
      queryClient.invalidateQueries({ queryKey: ['extra-advances'] });
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao cancelar bônus');
    },
  });

  // Recurring bonuses queries and mutations
  const { data: recurringData, refetch: refetchRecurring } = useQuery({
    queryKey: ['recurring-bonuses'],
    queryFn: () => recurringBonusesService.getRecurringBonuses({ isActive: 'true' }),
    staleTime: 0,
  });

  const recurringBonuses = useMemo(() => {
    if (!recurringData?.data?.data) return [];
    return Array.isArray(recurringData.data.data) ? recurringData.data.data : [];
  }, [recurringData]);

  const createRecurringMutation = useMutation({
    mutationFn: (data) => recurringBonusesService.createRecurringBonus(data),
    onSuccess: () => {
      toast.success('Bônus recorrente criado com sucesso!');
      queryClient.invalidateQueries({ queryKey: ['recurring-bonuses'] });
      setIsRecurringModalOpen(false);
      resetRecurringForm();
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao criar bônus recorrente');
    },
  });

  const updateRecurringMutation = useMutation({
    mutationFn: ({ id, data }) => recurringBonusesService.updateRecurringBonus(id, data),
    onSuccess: () => {
      toast.success('Bônus recorrente atualizado com sucesso!');
      queryClient.invalidateQueries({ queryKey: ['recurring-bonuses'] });
      setIsRecurringModalOpen(false);
      setEditingRecurring(null);
      resetRecurringForm();
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao atualizar bônus recorrente');
    },
  });

  const deleteRecurringMutation = useMutation({
    mutationFn: (id) => recurringBonusesService.deleteRecurringBonus(id),
    onSuccess: () => {
      toast.success('Bônus recorrente excluído com sucesso!');
      queryClient.invalidateQueries({ queryKey: ['recurring-bonuses'] });
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao excluir bônus recorrente');
    },
  });

  const toggleRecurringMutation = useMutation({
    mutationFn: ({ id, isActive }) => recurringBonusesService.toggleRecurringBonus(id, isActive),
    onSuccess: () => {
      toast.success('Status do bônus atualizado!');
      queryClient.invalidateQueries({ queryKey: ['recurring-bonuses'] });
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao atualizar status');
    },
  });

  const generateRecurringMutation = useMutation({
    mutationFn: ({ year, month }) => recurringBonusesService.generateRecurringBonuses(year, month),
    onSuccess: (data) => {
      const count = data?.data?.generated || 0;
      toast.success(`${count} bônus(s) gerado(s) com sucesso!`);
      queryClient.invalidateQueries({ queryKey: ['extra-advances'] });
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao gerar bônus');
    },
  });

  const handleGenerateRecurring = () => {
    generateRecurringMutation.mutate({ year: currentYear, month: currentMonth });
  };

  const resetForm = () => {
    setFormData({
      amount: '',
      request_date: new Date().toISOString().split('T')[0],
      deduction_month: new Date().getMonth() + 1,
      deduction_year: new Date().getFullYear(),
      description: '',
      notes: '',
    });
    setEditingAdvance(null);
  };

  const handleOpenModal = (advance = null) => {
    if (advance) {
      setEditingAdvance(advance);
      setSelectedUserId(advance.user_id || '');
      setFormData({
        amount: advance.amount || '',
        request_date: advance.request_date ? advance.request_date.split('T')[0] : new Date().toISOString().split('T')[0],
        deduction_month: advance.deduction_month || new Date().getMonth() + 1,
        deduction_year: advance.deduction_year || new Date().getFullYear(),
        description: advance.description || '',
        notes: advance.notes || '',
      });
    } else {
      resetForm();
      if (selectedUserId) {
        setFormData(prev => ({ ...prev, deduction_month: selectedMonth, deduction_year: selectedYear }));
      }
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingAdvance(null);
    resetForm();
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!selectedUserId || !formData.amount || !formData.deduction_month || !formData.deduction_year) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }

    const submitData = {
      user_id: selectedUserId,
      amount: Number(formData.amount),
      request_date: formData.request_date,
      deduction_month: Number(formData.deduction_month),
      deduction_year: Number(formData.deduction_year),
      description: formData.description || null,
      notes: formData.notes || null,
    };

    if (editingAdvance) {
      updateMutation.mutate({ id: editingAdvance.id, data: submitData });
    } else {
      createMutation.mutate(submitData);
    }
  };

  const handleDelete = (advance) => {
    setConfirmDelete(advance);
  };

  const handleConfirmDelete = () => {
    if (confirmDelete) {
      deleteMutation.mutate(confirmDelete.id);
      setConfirmDelete(null);
    }
  };

  const resetRecurringForm = () => {
    setRecurringFormData({
      userId: '',
      amount: '',
      description: '',
      startMonth: currentMonth,
      startYear: currentYear,
      endMonth: '',
      endYear: '',
    });
    setEditingRecurring(null);
  };

  const handleOpenRecurringModal = (bonus = null) => {
    if (bonus) {
      setEditingRecurring(bonus);
      setRecurringFormData({
        userId: bonus.user_id || '',
        amount: bonus.amount || '',
        description: bonus.description || '',
        startMonth: bonus.start_month || currentMonth,
        startYear: bonus.start_year || currentYear,
        endMonth: bonus.end_month || '',
        endYear: bonus.end_year || '',
      });
    } else {
      resetRecurringForm();
    }
    setIsRecurringModalOpen(true);
  };

  const handleCloseRecurringModal = () => {
    setIsRecurringModalOpen(false);
    setEditingRecurring(null);
    resetRecurringForm();
  };

  const handleSubmitRecurring = (e) => {
    e.preventDefault();

    if (!recurringFormData.userId || !recurringFormData.amount || !recurringFormData.startMonth || !recurringFormData.startYear) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }

    const submitData = {
      userId: Number(recurringFormData.userId),
      amount: Number(recurringFormData.amount),
      description: recurringFormData.description || null,
      startMonth: Number(recurringFormData.startMonth),
      startYear: Number(recurringFormData.startYear),
      endMonth: recurringFormData.endMonth ? Number(recurringFormData.endMonth) : null,
      endYear: recurringFormData.endYear ? Number(recurringFormData.endYear) : null,
    };

    if (editingRecurring) {
      updateRecurringMutation.mutate({ id: editingRecurring.id, data: submitData });
    } else {
      createRecurringMutation.mutate(submitData);
    }
  };

  const handleDeleteRecurring = (bonus) => {
    setConfirmDeleteRecurring(bonus);
  };

  const handleConfirmDeleteRecurring = () => {
    if (confirmDeleteRecurring) {
      deleteRecurringMutation.mutate(confirmDeleteRecurring.id);
      setConfirmDeleteRecurring(null);
    }
  };

  const handleToggleRecurring = (bonus) => {
    toggleRecurringMutation.mutate({ id: bonus.id, isActive: !bonus.is_active });
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('pt-BR');
  };

  const monthNames = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
  ];

  const getStatusBadge = (status) => {
    const badges = {
      PENDING: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Pendente', icon: AlertCircle },
      APPROVED: { bg: 'bg-green-100', text: 'text-green-800', label: 'Aprovado', icon: CheckCircle },
      CANCELLED: { bg: 'bg-red-100', text: 'text-red-800', label: 'Cancelado', icon: XCircle },
    };
    return badges[status] || badges.PENDING;
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <DollarSign className="w-6 h-6" />
          Área de Bonificação
        </h1>
        <p className="text-gray-600 mt-1">
          Gerencie extras para funcionários. O valor será adicionado ao holerite do mês selecionado.
        </p>
      </div>

      {/* Filtros e botão de adicionar */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <User className="w-4 h-4 inline mr-1" />
              Funcionário
            </label>
            <select
              value={selectedUserId}
              onChange={(e) => {
                setSelectedUserId(e.target.value);
                resetForm();
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Todos os funcionários</option>
              {users.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.full_name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Calendar className="w-4 h-4 inline mr-1" />
              Mês de Referência
            </label>
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(Number(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              {monthNames.map((name, index) => (
                <option key={index + 1} value={index + 1}>
                  {name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Ano de Referência</label>
            <input
              type="number"
              value={selectedYear}
              onChange={(e) => setSelectedYear(Number(e.target.value))}
              min="2020"
              max="2100"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div className="flex items-end">
            <button
              onClick={() => {
                if (!selectedUserId) {
                  toast.error('Selecione um funcionário primeiro');
                  return;
                }
                handleOpenModal();
              }}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Novo Bônus
            </button>
          </div>
        </div>
      </div>

      {/* Lista de bônus */}
      {!selectedUserId ? (
        <div className="bg-gray-50 rounded-lg p-8 text-center text-gray-500">
          <DollarSign className="w-12 h-12 mx-auto mb-4 text-gray-400" />
          <p>Selecione um funcionário para visualizar os Bônus</p>
        </div>
      ) : isLoading ? (
        <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
          Carregando...
        </div>
      ) : advances.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
          <DollarSign className="w-12 h-12 mx-auto mb-4 text-gray-400" />
          <p>Nenhum bônus registrado para {monthNames[selectedMonth - 1]}/{selectedYear}</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Funcionário
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Valor
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Data Solicitação
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Referente a
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Descrição
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {advances.map((advance) => {
                const badge = getStatusBadge(advance.status);
                const Icon = badge.icon;
                return (
                  <tr key={advance.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {advance.user_name}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-semibold text-gray-900">
                        {Number(advance.amount).toLocaleString('pt-BR', {
                          style: 'currency',
                          currency: 'BRL',
                        })}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {formatDate(advance.request_date)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {monthNames[advance.deduction_month - 1]}/{advance.deduction_year}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
                        <Icon className="w-3 h-3" />
                        {badge.label}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {advance.description || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end gap-2">
                        {advance.status === 'PENDING' && (
                          <>
                            <button
                              onClick={() => approveMutation.mutate(advance.id)}
                              className="text-green-600 hover:text-green-900"
                              title="Aprovar"
                              disabled={approveMutation.isPending}
                            >
                              <CheckCircle className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => rejectMutation.mutate(advance.id)}
                              className="text-orange-600 hover:text-orange-900"
                              title="Rejeitar"
                              disabled={rejectMutation.isPending}
                            >
                              <XCircle className="w-4 h-4" />
                            </button>
                          </>
                        )}
                        <button
                          onClick={() => handleOpenModal(advance)}
                          className="text-blue-600 hover:text-blue-900"
                          title="Editar"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(advance)}
                          className="text-red-600 hover:text-red-900"
                          title="Deletar"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Bônus Recorrentes */}
      <div className="mt-8 border-t pt-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <RefreshCw className="w-5 h-5" />
            Bônus Recorrentes
          </h2>
          <div className="flex items-center gap-2">
            <button
              onClick={handleGenerateRecurring}
              disabled={generateRecurringMutation.isPending}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 text-sm disabled:opacity-50"
              title="Força a geração dos bônus para o mês atual"
            >
              <RefreshCw className={`w-4 h-4 ${generateRecurringMutation.isPending ? 'animate-spin' : ''}`} />
              {generateRecurringMutation.isPending ? 'Gerando...' : 'Forçar Geração'}
            </button>
            <button
              onClick={() => handleOpenRecurringModal()}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2 text-sm"
            >
              <Plus className="w-4 h-4" />
              Novo Bônus Recorrente
            </button>
          </div>
        </div>

        {recurringBonuses.length === 0 ? (
          <div className="bg-gray-50 rounded-lg p-8 text-center text-gray-500">
            <RefreshCw className="w-12 h-12 mx-auto mb-4 text-gray-400" />
            <p>Nenhum bônus recorrente cadastrado</p>
            <p className="text-sm mt-2">Bônus recorrentes são gerados automaticamente todo mês</p>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Funcionário</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Valor Mensal</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Início</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fim</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Descrição</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {recurringBonuses.map((bonus) => (
                  <tr key={bonus.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {bonus.user_name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                      {Number(bonus.amount).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {monthNames[bonus.start_month - 1]}/{bonus.start_year}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {bonus.end_month ? `${monthNames[bonus.end_month - 1]}/${bonus.end_year}` : 'Indeterminado'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => handleToggleRecurring(bonus)}
                        disabled={toggleRecurringMutation.isPending}
                        className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          bonus.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {bonus.is_active ? (
                          <ToggleRight className="w-3 h-3" />
                        ) : (
                          <ToggleLeft className="w-3 h-3" />
                        )}
                        {bonus.is_active ? 'Ativo' : 'Inativo'}
                      </button>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {bonus.description || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleOpenRecurringModal(bonus)}
                          className="text-blue-600 hover:text-blue-900"
                          title="Editar"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteRecurring(bonus)}
                          className="text-red-600 hover:text-red-900"
                          title="Excluir"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal de criar/editar bônus */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-gray-900">
                  {editingAdvance ? 'Editar Bônus' : 'Novo Bônus'}
                </h2>
                <button
                  onClick={handleCloseModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleSubmit}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Funcionário *
                    </label>
                    <select
                      value={selectedUserId}
                      onChange={(e) => setSelectedUserId(e.target.value)}
                      required
                      disabled={!!editingAdvance}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                    >
                      <option value="">Selecione um funcionário</option>
                      {users.map((user) => (
                        <option key={user.id} value={user.id}>
                          {user.full_name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Valor do Bônus (R$) *
                    </label>
                    <div className="relative">
                      <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        type="number"
                        step="0.01"
                        min="0.01"
                        value={formData.amount}
                        onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                        placeholder="0.00"
                        required
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Data da Solicitação *
                    </label>
                    <input
                      type="date"
                      value={formData.request_date}
                      onChange={(e) => setFormData({ ...formData, request_date: e.target.value })}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Mês de Referência *
                      </label>
                      <select
                        value={formData.deduction_month}
                        onChange={(e) => setFormData({ ...formData, deduction_month: Number(e.target.value) })}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        {monthNames.map((name, index) => (
                          <option key={index + 1} value={index + 1}>
                            {name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Ano de Referência *
                      </label>
                      <input
                        type="number"
                        value={formData.deduction_year}
                        onChange={(e) => setFormData({ ...formData, deduction_year: Number(e.target.value) })}
                        min="2020"
                        max="2100"
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Descrição
                    </label>
                    <input
                      type="text"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="Ex: Bônus por desempenho"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Observações
                    </label>
                    <textarea
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      rows="3"
                      placeholder="Observações adicionais..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <p className="text-xs text-blue-800">
                      <strong>Importante:</strong> O valor será adicionado automaticamente ao holerite de {monthNames[formData.deduction_month - 1]}/{formData.deduction_year}.
                    </p>
                  </div>
                </div>

                <div className="flex items-center justify-end gap-3 mt-6">
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={createMutation.isLoading || updateMutation.isLoading}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    <Save className="w-4 h-4" />
                    {createMutation.isLoading || updateMutation.isLoading ? 'Salvando...' : 'Salvar'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Modal de criar/editar bônus recorrente */}
      {isRecurringModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-gray-900">
                  {editingRecurring ? 'Editar Bônus Recorrente' : 'Novo Bônus Recorrente'}
                </h2>
                <button
                  onClick={handleCloseRecurringModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleSubmitRecurring}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Funcionário *
                    </label>
                    <select
                      value={recurringFormData.userId}
                      onChange={(e) => setRecurringFormData({ ...recurringFormData, userId: e.target.value })}
                      required
                      disabled={!!editingRecurring}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 disabled:bg-gray-100"
                    >
                      <option value="">Selecione um funcionário</option>
                      {users.map((user) => (
                        <option key={user.id} value={user.id}>
                          {user.full_name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Valor Mensal (R$) *
                    </label>
                    <div className="relative">
                      <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        type="number"
                        step="0.01"
                        min="0.01"
                        value={recurringFormData.amount}
                        onChange={(e) => setRecurringFormData({ ...recurringFormData, amount: e.target.value })}
                        placeholder="0.00"
                        required
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Descrição
                    </label>
                    <input
                      type="text"
                      value={recurringFormData.description}
                      onChange={(e) => setRecurringFormData({ ...recurringFormData, description: e.target.value })}
                      placeholder="Ex: Bônus de produtividade"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Mês de Início *
                      </label>
                      <select
                        value={recurringFormData.startMonth}
                        onChange={(e) => setRecurringFormData({ ...recurringFormData, startMonth: Number(e.target.value) })}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      >
                        {monthNames.map((name, index) => (
                          <option key={index + 1} value={index + 1}>
                            {name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Ano de Início *
                      </label>
                      <input
                        type="number"
                        value={recurringFormData.startYear}
                        onChange={(e) => setRecurringFormData({ ...recurringFormData, startYear: Number(e.target.value) })}
                        min="2020"
                        max="2100"
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Mês de Fim (opcional)
                      </label>
                      <select
                        value={recurringFormData.endMonth}
                        onChange={(e) => setRecurringFormData({ ...recurringFormData, endMonth: Number(e.target.value) || '' })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      >
                        <option value="">Sem fim definido</option>
                        {monthNames.map((name, index) => (
                          <option key={index + 1} value={index + 1}>
                            {name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Ano de Fim (opcional)
                      </label>
                      <input
                        type="number"
                        value={recurringFormData.endYear}
                        onChange={(e) => setRecurringFormData({ ...recurringFormData, endYear: Number(e.target.value) || '' })}
                        min="2020"
                        max="2100"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      />
                    </div>
                  </div>

                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                    <p className="text-xs text-purple-800">
                      <strong>Importante:</strong> O bônus será gerado automaticamente todo dia 1 do mês para todos os funcionários ativos com bônus recorrente ativo.
                    </p>
                  </div>
                </div>

                <div className="flex items-center justify-end gap-3 mt-6">
                  <button
                    type="button"
                    onClick={handleCloseRecurringModal}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={createRecurringMutation.isLoading || updateRecurringMutation.isLoading}
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    <Save className="w-4 h-4" />
                    {createRecurringMutation.isLoading || updateRecurringMutation.isLoading ? 'Salvando...' : 'Salvar'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={!!confirmDelete}
        title="Confirmar Exclusão"
        message={`Tem certeza que deseja deletar o bônus de ${confirmDelete?.user_name} no valor de R$ ${confirmDelete ? Number(confirmDelete.amount).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : ''}?`}
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmDelete(null)}
      />

      <ConfirmModal
        isOpen={!!confirmDeleteRecurring}
        title="Confirmar Exclusão"
        message="Tem certeza que deseja excluir este bônus recorrente?"
        onConfirm={handleConfirmDeleteRecurring}
        onCancel={() => setConfirmDeleteRecurring(null)}
      />
    </div>
  );
};

export default ExtraAdvancesManagement;
